import { type QueryClient } from '@tanstack/react-query';
import {
  type CONVEYANCE_TYPES,
  type PaymentDetails,
  type SubmitBasketData,
  type Basket,
  type BasketOrder,
} from '@koala/sdk';
import { type InitializationErrorTypes } from '@/constants/basket';
import { BASKET_ERROR } from '@/redux/basket/messages';
import {
  type BasketNotFoundSource,
  type BasketPricing,
  type OrderStatusMessageAction,
  type PreAuthValidation,
  type Recovery502Endpoint,
  type SubmitFailureCause,
} from '@/types/orderStatus';
import { type CommitConfirmation } from '@/features/handoff/time-picker/commit-confirmation';
import { type ToastRecoveryCategory } from '@/utils/toastErrorClassifier';

const orderStatusActions = {
  ORDER_STATUS_MESSAGE_CLEAR: '/koala/orderStatus/ORDER_STATUS_MESSAGE_CLEAR',
  ORDER_STATUS_MESSAGE_ERROR_SET: '/koala/orderStatus/ORDER_STATUS_MESSAGE_ERROR_SET',

  ORDER_NOT_INITIALIZED: '/koala/orderStatus/ORDER_NOT_INITIALIZED',
  ORDER_PENDING: '/koala/orderStatus/ORDER_PENDING',

  INITIALIZE_ORDER: '/koala/orderStatus/INITIALIZE_ORDER',
  INITIALIZE_ORDER_ERROR: '/koala/orderStatus/INITIALIZE_ORDER_ERROR',
  INITIALIZE_ORDER_RECONCILIATION: '/koala/orderStatus/INITIALIZE_ORDER_RECONCILIATION',
  INITIALIZE_ORDER_RESUME: '/koala/orderStatus/INITIALIZE_ORDER_RESUME',
  POST_INITIALIZE_ORDER: '/koala/orderStatus/POST_INITIALIZE_ORDER',
  DESTROY_ORDER: '/koala/orderStatus/DESTROY_ORDER',

  VALIDATE_ORDER: '/koala/orderStatus/VALIDATE_ORDER',
  PRE_AUTH_VALIDATE: '/koala/orderStatus/PRE_AUTH_VALIDATE',
  PRE_AUTH_VALIDATE_SETTLED: '/koala/orderStatus/PRE_AUTH_VALIDATE_SETTLED',
  BASKET_PRICING_SETTLED: '/koala/orderStatus/BASKET_PRICING_SETTLED',

  PROMO_CODE_ADD: '/koala/orderStatus/PROMO_CODE_ADD',
  PROMO_CODE_REMOVE: '/koala/orderStatus/PROMO_CODE_REMOVE',

  BASKET_REWARDS_GET: '/koala/orderStatus/BASKET_REWARDS_GET',
  BASKET_REWARDS_ADD: '/koala/orderStatus/BASKET_REWARDS_ADD',
  BASKET_REWARDS_REMOVE: '/koala/orderStatus/BASKET_REWARDS_REMOVE',

  TIP_ADD: '/koala/orderStatus/TIP_ADD',

  UTENSILS_SET: '/koala/orderStatus/UTENSILS_SET',

  BASKET_INSTRUCTIONS_SET: '/koala/orderStatus/BASKET_INSTRUCTIONS_SET',

  CONVEYANCE_SET: '/koala/orderStatus/CONVEYANCE_SET',
  CONVEYANCE_DELIVERY_SETTING: '/koala/orderStatus/CONVEYANCE_DELIVERY_SETTING',
  CONVEYANCE_DELIVERY_SET: '/koala/orderStatus/CONVEYANCE_DELIVERY_SET',

  WANTED_AT_BASKET_SET: '/koala/orderStatus/WANTED_AT_BASKET_SET',
  WANTED_AT_BASKET_REMOVE: '/koala/orderStatus/WANTED_AT_BASKET_REMOVE',

  ORDER_TIME_UNAVAILABLE: '/koala/orderStatus/ORDER_TIME_UNAVAILABLE',

  ORDER_SUBMISSION_TRIGGER: '/koala/orderStatus/ORDER_SUBMISSION_TRIGGER',
  ORDER_SUBMISSION_PCI_ERROR: '/koala/orderStatus/ORDER_SUBMISSION_PCI_ERROR',
  ORDER_SUBMISSION_PCI_FAILURE: '/koala/orderStatus/ORDER_SUBMISSION_PCI_FAILURE',
  ORDER_SUBMISSION_START: '/koala/orderStatus/ORDER_SUBMISSION_START',
  ORDER_SUBMISSION_ERROR: '/koala/orderStatus/ORDER_SUBMISSION_ERROR',
  ORDER_SUBMISSION_PAGE_ERROR: '/koala/orderStatus/ORDER_SUBMISSION_PAGE_ERROR',
  RECOVERY_502_TRIGGERED: '/koala/orderStatus/RECOVERY_502_TRIGGERED',

  STORED_VALUE_POST: '/koala/orderStatus/STORED_VALUE_POST',

  DIGITAL_WALLET_ORDER_STARTED: '/koala/orderStatus/DIGITAL_WALLET_ORDER_STARTED',
  ORDER_SUBMISSION_DIGITAL_WALLET_TRIGGER:
    '/koala/orderStatus/ORDER_SUBMISSION_DIGITAL_WALLET_TRIGGER',
  PAYMENT_APPLYING: '/koala/orderStatus/PAYMENT_APPLYING',
  TOAST_PAYMENT_RECOVERY: '/koala/orderStatus/TOAST_PAYMENT_RECOVERY',

  DELIVERY_UNAVAILABLE: '/koala/orderStatus/DELIVERY_UNAVAILABLE',
  RESTAURANT_UNAVAILABLE: '/koala/orderStatus/RESTAURANT_UNAVAILABLE',
  PRICING_UNAVAILABLE: '/koala/orderStatus/PRICING_UNAVAILABLE',
  BASKET_NOT_FOUND: '/koala/orderStatus/BASKET_NOT_FOUND',

  orderStatusMessageClear: () => ({
    type: orderStatusActions.ORDER_STATUS_MESSAGE_CLEAR,
  }),
  orderStatusMessageErrorSet: (message: string, messageAction?: OrderStatusMessageAction) => ({
    type: orderStatusActions.ORDER_STATUS_MESSAGE_ERROR_SET,
    message,
    messageAction,
  }),
  orderPending: () => ({
    type: orderStatusActions.ORDER_PENDING,
  }),
  forceOrderPending: () => ({
    type: orderStatusActions.ORDER_PENDING,
    force: true,
  }),
  orderNotInitialized: () => ({
    type: orderStatusActions.ORDER_NOT_INITIALIZED,
  }),
  initializeOrder: (basketOrder: BasketOrder, locationId: number, wantedAt?: string) => ({
    type: orderStatusActions.INITIALIZE_ORDER,
    basketOrder,
    locationId,
    wantedAt,
  }),
  /**
   * `message` is diner-safe copy; `initializationErrorType` is how the reconciliation modal should
   * treat the failure. TEAM-13882: the two are passed separately because the modal used to re-derive
   * the second from the first by substring-matching the copy it was about to render.
   * The classification is required so a caller cannot silently fall back to Restart Order.
   */
  /**
   * `requiredOption` (TEAM-15138) carries the cart line and option group for
   * `InitializationErrorTypes.REQUIRED_OPTION`. Optional because every other classification
   * dispatches this action without one.
   */
  initializeOrderReconciliation: (
    message: string,
    initializationErrorType: InitializationErrorTypes,
    requiredOption?: { basketItemIndex: number | null; groupName: string | null },
  ) => ({
    type: orderStatusActions.INITIALIZE_ORDER_RECONCILIATION,
    message,
    initializationErrorType,
    requiredOption,
  }),
  initializeOrderResume: () => ({
    type: orderStatusActions.INITIALIZE_ORDER_RESUME,
  }),
  postInitializeOrder: (
    basketOrder: BasketOrder,
    basket: Basket,
    fulfillmentType: CONVEYANCE_TYPES,
    wantedAtDay?: string,
    wantedAt?: string,
  ) => ({
    type: orderStatusActions.POST_INITIALIZE_ORDER,
    basketOrder,
    basket,
    fulfillmentType,
    wantedAtDay,
    wantedAt,
  }),
  destroyOrder: () => ({
    type: orderStatusActions.DESTROY_ORDER,
  }),
  validateOrder: (basketId: string, locationId: number, rearmOnFailure = false) => ({
    type: orderStatusActions.VALIDATE_ORDER,
    basketId,
    locationId,
    // TEAM-14818: opt a caller into re-arming ORDER_PENDING after a generic refresh failure, so a
    // failed re-validate never strands the diner on "Order updating....". Defaults false → the
    // fail-closed behavior TEAM-14666 locked in stays unchanged for every existing caller.
    rearmOnFailure,
  }),
  /**
   * TEAM-14644 (UAT follow-up): re-validate the basket BEFORE a retry authorizes a payment
   * amount, for the payment flows that commit the amount before ORDER_SUBMISSION_TRIGGER
   * is ever dispatched (Toast) and so can never be caught by the pre-resubmit gate inside
   * `orderSubmissionTriggerSaga`.
   *
   * Separate from VALIDATE_ORDER because the caller has to know the OUTCOME: it only
   * authorizes if the basket came back clean. That cannot be inferred from
   * `orderStatus.status`/`type` — a generic failure that re-arms the CTA settles on exactly
   * the ORDER_PENDING/OK a success does, and reading it as success would authorize against
   * a basket the validate could not refresh.
   */
  preAuthValidate: (basketId: string, locationId: number) => ({
    type: orderStatusActions.PRE_AUTH_VALIDATE,
    basketId,
    locationId,
  }),
  preAuthValidateSettled: (verdict: Exclude<PreAuthValidation, 'pending'>) => ({
    type: orderStatusActions.PRE_AUTH_VALIDATE_SETTLED,
    verdict,
  }),
  /** TEAM-14787: record whether a validate priced `basketId`. See `IOrderStatusState.basketPricing`. */
  basketPricingSettled: (
    basketId: string,
    outcome: BasketPricing['outcome'],
    failedStep?: BasketPricing['failedStep'],
  ) => ({
    type: orderStatusActions.BASKET_PRICING_SETTLED,
    basketId,
    outcome,
    failedStep: failedStep ?? null,
  }),
  promoCodeAdd: (promoCode: string, options?: { bypassBasketMutationGuard?: boolean }) => ({
    type: orderStatusActions.PROMO_CODE_ADD,
    promoCode,
    bypassBasketMutationGuard: options?.bypassBasketMutationGuard,
  }),
  promoCodeRemove: () => ({
    type: orderStatusActions.PROMO_CODE_REMOVE,
  }),
  conveyanceSet: () => ({
    type: orderStatusActions.CONVEYANCE_SET,
  }),
  conveyanceDeliverySetting: () => ({
    type: orderStatusActions.CONVEYANCE_DELIVERY_SETTING,
  }),
  conveyanceDeliverySet: (instructions?: string, staleId?: number) => ({
    type: orderStatusActions.CONVEYANCE_DELIVERY_SET,
    instructions,
    staleId,
  }),
  /**
   * `confirmation` is dispatched as a success toast by wantedAtBasketSetSaga, and
   * only from its success branch. The commit is a network call consumed by
   * takeLatest, so the dispatching component never learns the outcome — passing the
   * copy through means the confirmation cannot fire ahead of a failure (TEAM-14387).
   */
  wantedAtBasketSet: (
    wantedAt: string,
    basketId?: string,
    locationId?: number,
    confirmation?: CommitConfirmation,
  ) => ({
    type: orderStatusActions.WANTED_AT_BASKET_SET,
    wantedAt,
    basketId,
    locationId,
    confirmation,
  }),
  wantedAtBasketRemove: (basketId?: string, locationId?: number) => ({
    type: orderStatusActions.WANTED_AT_BASKET_REMOVE,
    basketId,
    locationId,
  }),
  orderTimeUnavailableSet: () => ({
    type: orderStatusActions.ORDER_TIME_UNAVAILABLE,
    message: BASKET_ERROR.orderTimeUnavailable,
  }),
  basketRewardsGet: () => ({
    type: orderStatusActions.BASKET_REWARDS_GET,
  }),
  basketRewardsAdd: () => ({
    type: orderStatusActions.BASKET_REWARDS_ADD,
  }),
  basketRewardsRemove: () => ({
    type: orderStatusActions.BASKET_REWARDS_REMOVE,
  }),
  tipAdd: (tipAmount: number, isCustom: boolean) => ({
    type: orderStatusActions.TIP_ADD,
    tipAmount,
    isCustom,
  }),
  utensilsSet: (utensils: boolean) => ({
    type: orderStatusActions.UTENSILS_SET,
    utensils,
  }),
  basketInstructionsSet: (basketInstructions: { instructions: string }) => ({
    type: orderStatusActions.BASKET_INSTRUCTIONS_SET,
    basketInstructions,
  }),
  storedValuePost: (cardToken: string, cardNumber: string, pin?: string, captcha?: string) => ({
    type: orderStatusActions.STORED_VALUE_POST,
    cardToken,
    cardNumber,
    pin,
    captcha,
  }),
  orderSubmissionTrigger: (captcha?: string | null, queryClient?: QueryClient) => ({
    type: orderStatusActions.ORDER_SUBMISSION_TRIGGER,
    captcha,
    queryClient,
  }),
  orderSubmissionPciError: (message: string) => ({
    type: orderStatusActions.ORDER_SUBMISSION_PCI_ERROR,
    message,
  }),
  orderSubmissionPciFailure: () => ({
    type: orderStatusActions.ORDER_SUBMISSION_PCI_FAILURE,
  }),
  orderSubmissionStart: (
    submitBasketData: SubmitBasketData,
    captcha?: string | null,
    queryClient?: QueryClient,
  ) => ({
    type: orderStatusActions.ORDER_SUBMISSION_START,
    submitBasketData,
    captcha,
    queryClient,
  }),
  orderSubmissionError: (
    message: string,
    cause?: SubmitFailureCause,
    messageAction?: OrderStatusMessageAction,
    toastPaymentWasAuthorized?: boolean,
  ) => ({
    type: orderStatusActions.ORDER_SUBMISSION_ERROR,
    message,
    cause,
    messageAction,
    toastPaymentWasAuthorized,
  }),
  orderSubmissionPageError: (
    message: string,
    cause?: SubmitFailureCause,
    toastPaymentWasAuthorized?: boolean,
  ) => ({
    type: orderStatusActions.ORDER_SUBMISSION_PAGE_ERROR,
    message,
    cause,
    toastPaymentWasAuthorized,
  }),
  recovery502Triggered: (endpoint: Recovery502Endpoint, toastPaymentWasAuthorized = false) => ({
    type: orderStatusActions.RECOVERY_502_TRIGGERED,
    endpoint,
    toastPaymentWasAuthorized,
  }),
  digitalWalletOrderStarted: () => ({
    type: orderStatusActions.DIGITAL_WALLET_ORDER_STARTED,
  }),
  orderDigitalWalletTrigger: (
    paymentDetails: PaymentDetails,
    captcha?: string | null,
    queryClient?: QueryClient,
  ) => ({
    type: orderStatusActions.ORDER_SUBMISSION_DIGITAL_WALLET_TRIGGER,
    paymentDetails,
    captcha,
    queryClient,
  }),
  paymentApplying: () => ({
    type: orderStatusActions.PAYMENT_APPLYING,
  }),
  deliveryUnavailable: () => ({
    type: orderStatusActions.DELIVERY_UNAVAILABLE,
  }),
  toastPaymentRecovery: (category: ToastRecoveryCategory, message: string) => ({
    type: orderStatusActions.TOAST_PAYMENT_RECOVERY,
    category,
    message,
  }),
  /**
   * TEAM-14642: carries the reason the service gave, so the blocking modal can show
   * it. Optional on purpose — callers with no reason to hand keep today's behaviour
   * and the modal falls back to its own copy.
   */
  restaurantUnavailable: (message?: string, toastPaymentWasAuthorized = false) => ({
    type: orderStatusActions.RESTAURANT_UNAVAILABLE,
    message,
    toastPaymentWasAuthorized,
  }),
  /**
   * TEAM-14493: the restaurant's POS could not price the order (FPO code 13). Carries no message on
   * purpose — the modal's copy is product-authored and names the restaurant from basket state, so
   * nothing upstream is ever rendered for this failure.
   */
  pricingUnavailable: () => ({
    type: orderStatusActions.PRICING_UNAVAILABLE,
  }),
  /**
   * TEAM-15690: coo-api answered 404 for `basketId`. Carries no message: the notice's copy is
   * product-authored and names the restaurant from basket state. `toastPaymentWasAuthorized`
   * (TEAM-14291) makes the notice append the card-hold note.
   */
  basketNotFound: (
    basketId: string,
    source: BasketNotFoundSource,
    toastPaymentWasAuthorized = false,
  ) => ({
    type: orderStatusActions.BASKET_NOT_FOUND,
    basketId,
    source,
    toastPaymentWasAuthorized,
  }),
};

export default orderStatusActions;
